import React, { useState, useRef, useEffect } from "react";
import { Employee, PersonaExpanded, getDefaultUserAvatar } from "winslow-core";
import TextInput from "./TextInput";

import "./CirclePersonaEmployeeSelect.css";

// Utility function to validate image URL
const validateImageUrl = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!url || url.startsWith("data:")) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;

    // Timeout after 3 seconds
    setTimeout(() => resolve(false), 3000);
  });
};

// Hook to manage image validation and fallback
const useValidatedImageUrl = (imageUrl: string, fallbackUrl: string) => {
  const [validatedUrl, setValidatedUrl] = useState<string>(fallbackUrl);
  const [isValidating, setIsValidating] = useState<boolean>(false);

  useEffect(() => {
    if (!imageUrl || imageUrl === fallbackUrl) {
      setValidatedUrl(fallbackUrl);
      return;
    }

    setIsValidating(true);
    validateImageUrl(imageUrl)
      .then((isValid) => {
        setValidatedUrl(isValid ? imageUrl : fallbackUrl);
      })
      .catch(() => {
        setValidatedUrl(fallbackUrl);
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [imageUrl, fallbackUrl]);

  return { validatedUrl, isValidating };
};

type CirclePersonaEmployeeSelectProps = {
  onChange: (value: string, isPersona: boolean) => void;
  includeNoFilter?: boolean;
  selectedEmployee?: string;
  selectedPersona?: string;

  presetEmployeeId?: string;
  employees?: Employee[];
  personas?: PersonaExpanded[];
  disabled?: boolean;
};

export const CirclePersonaEmployeeSelect = ({
  onChange,
  selectedEmployee,
  selectedPersona,

  includeNoFilter = false,
  presetEmployeeId,
  employees,
  personas,
  disabled = false,
}: CirclePersonaEmployeeSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
    imageUrl?: string;
    isPersona: boolean;
  } | null>(null);

  const selectorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Add effect to handle presetEmployeeId
  useEffect(() => {
    if (presetEmployeeId && employees && employees.length > 0) {
      const employee = employees.find((e) => e.id === presetEmployeeId);
      if (employee) {
        onChange(presetEmployeeId, false);
        setSelectedOption({
          value: employee.id,
          label: employee.displayName || employee.email || "Unknown Employee",
          imageUrl:
            employee?.profile?.avatar ||
            employee?.avatar ||
            employee?.data?.avatar ||
            getDefaultUserAvatar(employee.displayName || employee.email || ""),
          isPersona: false,
        });
      }
    }
  }, [presetEmployeeId, employees, onChange]);

  useEffect(() => {
    console.log("isOpen", isOpen);
  }, [isOpen]);

  // Initial selected value setup
  useEffect(() => {
    if (selectedEmployee && employees && employees.length > 0) {
      const employee = employees.find((e) => e.id === selectedEmployee);
      if (employee) {
        setSelectedOption({
          value: employee.id,
          label: employee.displayName || employee.email || "Unknown Employee",
          imageUrl:
            employee?.profile?.avatar ||
            employee?.avatar ||
            employee?.data?.avatar ||
            getDefaultUserAvatar(employee.displayName || employee.email || ""),
          isPersona: false,
        });
      }
    } else if (selectedPersona && personas && personas.length > 0) {
      const persona = personas.find((p) => p.id === selectedPersona);
      if (persona) {
        setSelectedOption({
          value: persona.id,
          label: persona.name || "Unknown Persona",
          imageUrl: "/icons/light-mode/persona_manager_off.svg",
          isPersona: true,
        });
      }
    } else {
      setSelectedOption(null);
    }
  }, [selectedEmployee, selectedPersona, employees, personas]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        selectorRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  console.log("[CirclePersonaEmployeeSelect] employees", employees);
  console.log("[CirclePersonaEmployeeSelect] personas", personas);

  // Convert employees to option format and filter by search term
  const employeeOptions = employees
    ? employees
        .map((employee) => {
          const fallbackAvatar = getDefaultUserAvatar(
            employee.displayName || employee.email || ""
          );
          const potentialImageUrl =
            employee?.profile?.avatar ||
            employee?.avatar ||
            employee?.data?.avatar;

          return {
            value: employee.id,
            label: employee.displayName || employee.email || "Unknown Employee",
            imageUrl: potentialImageUrl || fallbackAvatar,
            fallbackAvatar,
            isPersona: false,
          };
        })
        .filter(
          (option) =>
            option.label &&
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
    : [];

  // Map personas to options and filter by search term
  const personaOptions = personas
    ? personas
        .map((persona) => ({
          value: persona.id,
          label: persona.name || "Unknown Persona",
          imageUrl: "/icons/light-mode/persona_manager_off.svg",
          isPersona: true,
        }))
        .filter(
          (option) =>
            option.label &&
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .sort((a, b) => (a.label || "").localeCompare(b.label || ""))
    : [];

  const handleToggleMenu = () => {
    // Allow opening even when disabled/HRIS not connected to enable persona selection
    setIsOpen(!isOpen);
  };

  const handleSelect = (option: typeof selectedOption) => {
    if (option === null) {
      onChange("", false);
      setSelectedOption(null);
    } else {
      onChange(option.value, option.isPersona);
      setSelectedOption(option);
    }
    setIsOpen(false);
    setSearchTerm(""); // Clear search when selection is made
  };

  // Component for validated image display
  const ValidatedImage = ({
    imageUrl,
    fallbackUrl,
    alt,
    className,
  }: {
    imageUrl: string;
    fallbackUrl: string;
    alt: string;
    className: string;
  }) => {
    const { validatedUrl, isValidating } = useValidatedImageUrl(
      imageUrl,
      fallbackUrl
    );

    if (isValidating) {
      return <div className={`${className} loading`}>...</div>;
    }

    if (validatedUrl === fallbackUrl) {
      return <img src={fallbackUrl} alt={alt} className={className} />;
    }

    return <img src={validatedUrl} alt={alt} className={className} />;
  };

  return (
    <>
      <div className="circle-employee-selector" ref={selectorRef}>
        <div
          className={`circle-selector ${selectedOption ? "selected" : "empty"}`}
          onClick={handleToggleMenu}>
          {selectedOption ? (
            <>
              <ValidatedImage
                imageUrl={selectedOption.imageUrl || ""}
                fallbackUrl={getDefaultUserAvatar(selectedOption.label)}
                alt={selectedOption.label}
                className="circle-avatar"
              />
              <span className="selected-label">{selectedOption.label}</span>
              <div
                className="clear-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(null);
                }}>
                ×
              </div>
            </>
          ) : (
            <div className="placeholder-icon">
              <svg
                width="25"
                height="25"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <circle
                  cx="12.5"
                  cy="12.5"
                  r="12.1"
                  fill="#FBFCFD"
                  stroke="#EBECF0"
                  strokeWidth="0.8"
                />
                <path
                  d="M10.9999 14.3333H9.99992C9.06954 14.3333 8.60435 14.3333 8.22582 14.4482C7.37356 14.7067 6.70661 15.3736 6.44808 16.2259C6.33325 16.6044 6.33325 17.0696 6.33325 18M14.6666 9C14.6666 10.6569 13.3234 12 11.6666 12C10.0097 12 8.66659 10.6569 8.66659 9C8.66659 7.34315 10.0097 6 11.6666 6C13.3234 6 14.6666 7.34315 14.6666 9ZM12.3333 18L14.4008 17.4093C14.4998 17.381 14.5493 17.3668 14.5955 17.3456C14.6365 17.3268 14.6755 17.3039 14.7119 17.2772C14.7528 17.2471 14.7892 17.2107 14.862 17.1379L19.1666 12.8334C19.6269 12.3731 19.6269 11.6269 19.1666 11.1666C18.7064 10.7064 17.9602 10.7064 17.4999 11.1667L13.1954 15.4712C13.1226 15.544 13.0861 15.5804 13.0561 15.6214C13.0294 15.6578 13.0064 15.6967 12.9876 15.7377C12.9664 15.7839 12.9523 15.8334 12.924 15.9324L12.3333 18Z"
                  stroke="#FF0909"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>

        {isOpen && (
          <div className="circle-dropdown-menu" ref={menuRef}>
            {/* Search input at the top */}
            <div
              className="search-container"
              style={{ padding: "8px", borderBottom: "1px solid #EBECF0" }}>
              <TextInput
                title="Search"
                value={searchTerm}
                setValue={setSearchTerm}
                placeholder="Search employees and personas..."
                disabled={false}
              />
            </div>

            {includeNoFilter && (
              <div className="menu-section">
                <div className="option" onClick={() => handleSelect(null)}>
                  <span>No context</span>
                </div>
              </div>
            )}

            {employeeOptions.length > 0 && (
              <div className="menu-section">
                <div className="section-label">Employees</div>
                {employeeOptions.map((option) => (
                  <div
                    key={option.value}
                    className="option"
                    onClick={() => handleSelect(option)}>
                    <ValidatedImage
                      imageUrl={option.imageUrl || ""}
                      fallbackUrl={option.fallbackAvatar}
                      alt={option.label}
                      className="option-avatar"
                    />
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            )}

            {personaOptions.length > 0 && (
              <div className="menu-section">
                <div className="section-label">Personas</div>
                {personaOptions.map((option) => (
                  <div
                    key={option.value}
                    className="option"
                    onClick={() => handleSelect(option)}>
                    <ValidatedImage
                      imageUrl={option.imageUrl || ""}
                      fallbackUrl="/icons/light-mode/persona_manager_off.svg"
                      alt={option.label}
                      className="option-avatar"
                    />
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Show message when no results found */}
            {searchTerm &&
              employeeOptions.length === 0 &&
              personaOptions.length === 0 && (
                <div className="menu-section">
                  <div
                    className="option"
                    style={{ color: "#666", fontStyle: "italic" }}>
                    No results found for "{searchTerm}"
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default CirclePersonaEmployeeSelect;
